/**
 * Specification List Component
 * @implements FS-004.5.1 - View Specification List
 * @implements US-029 - Basic Specification Viewing
 *
 * Displays all specifications within a project with their status.
 * Provides navigation to specification details.
 */

import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  EditNote as EditNoteIcon,
} from '@mui/icons-material';
import { useQuery, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

/**
 * GraphQL query for fetching specifications
 * @implements FS-004.5.1 - View Specification List
 */
const GET_SPECIFICATIONS_QUERY = gql`
  query GetSpecifications($projectId: ID!) {
    specifications(projectId: $projectId) {
      id
      projectId
      specificationNumber
      formattedNumber
      title
      status
      createdAt
      updatedAt
    }
  }
`;

/**
 * Specification status enum
 * @implements BS-004.4 - Specification Status Reflects Control State
 */
export type SpecificationStatus = 'DRAFT' | 'FINAL';

/**
 * Specification list item type
 */
export interface SpecificationListItem {
  id: string;
  projectId: string;
  specificationNumber: number;
  formattedNumber: string;
  title: string;
  status: SpecificationStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * Response type for GetSpecifications query
 */
interface GetSpecificationsResponse {
  specifications: SpecificationListItem[];
}

/**
 * Variables for GetSpecifications query
 */
interface GetSpecificationsVariables {
  projectId: string;
}

/**
 * Props for SpecificationList component
 */
export interface SpecificationListProps {
  /** Project ID to fetch specifications for */
  projectId: string;
  /** Optional callback when a specification is clicked */
  onSpecificationClick?: (specificationId: string) => void;
}

/**
 * Status badge component for specifications
 * @implements FS-004.5.1 - Draft specifications visually distinguished
 */
const StatusBadge = ({ status }: { status: SpecificationStatus }) => {
  const isDraft = status === 'DRAFT';

  return (
    <Chip
      label={status}
      size="small"
      color={isDraft ? 'warning' : 'success'}
      variant={isDraft ? 'outlined' : 'filled'}
      icon={isDraft ? <EditNoteIcon /> : undefined}
      sx={{ fontWeight: 500, fontSize: '0.75rem' }}
    />
  );
};

/**
 * SpecificationList - Displays all specifications for a project
 *
 * @implements FS-004.5.1 - View Specification List
 * @implements US-029 - Basic Specification Viewing
 *
 * Features:
 * - Lists specifications sorted by specification number
 * - Shows FS-XXX number, title, and status
 * - Visual distinction for draft vs final specifications
 * - Empty state when no specifications exist
 * - Handles loading and error states
 */
export const SpecificationList = ({
  projectId,
  onSpecificationClick,
}: SpecificationListProps) => {
  const navigate = useNavigate();

  // Fetch specifications
  const { data, loading, error } = useQuery<
    GetSpecificationsResponse,
    GetSpecificationsVariables
  >(GET_SPECIFICATIONS_QUERY, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
  });

  const specifications = data?.specifications ?? [];

  // Handle click on specification
  const handleSpecificationClick = (specificationId: string) => {
    if (onSpecificationClick) {
      onSpecificationClick(specificationId);
    } else {
      // Default: navigate to specification detail
      navigate(`/projects/${projectId}/specifications/${specificationId}`);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Render loading state
  if (loading && !data) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load specifications: {error.message}
      </Alert>
    );
  }

  // Render empty state
  // @implements US-029 - Empty state when no specifications exist
  if (specifications.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <DescriptionIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          No Specifications Yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Specifications will appear here once they are created through
          conversations with the AI PM.
        </Typography>
      </Paper>
    );
  }

  // Render specification list
  // @implements US-029 - List sorted by specification number
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <List disablePadding>
        {specifications.map((spec, index) => (
          <ListItem
            key={spec.id}
            disablePadding
            divider={index < specifications.length - 1}
          >
            <ListItemButton
              onClick={() => handleSpecificationClick(spec.id)}
              sx={{ py: 2, px: 3 }}
            >
              <ListItemText
                primary={
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 0.5 }}
                  >
                    {/* Specification Number */}
                    <Typography
                      variant="subtitle2"
                      component="span"
                      sx={{
                        fontFamily: 'monospace',
                        bgcolor: 'primary.50',
                        color: 'primary.main',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontWeight: 600,
                      }}
                    >
                      {spec.formattedNumber}
                    </Typography>
                    {/* Title */}
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{ fontWeight: 500 }}
                    >
                      {spec.title}
                    </Typography>
                    {/* Status Badge */}
                    <StatusBadge status={spec.status} />
                  </Stack>
                }
                secondary={
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="span"
                  >
                    Updated {formatDate(spec.updatedAt)}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};
